import type { RangeResponse, Source } from 'pmtiles';

const publicRoot = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
const BASEMAP_ROOT = `${publicRoot}/maps/basemap`;

export const BASEMAP_STYLE_URL = `${BASEMAP_ROOT}/style.json`;
export type MapLibreLibrary = typeof import('maplibre-gl');

class NamedArchiveSource implements Source {
  constructor(private readonly name: string, private readonly source: Source) {}

  getKey(): string {
    return this.name;
  }

  getBytes(offset: number, length: number, signal?: AbortSignal, etag?: string): Promise<RangeResponse> {
    return this.source.getBytes(offset, length, signal, etag);
  }
}

let dependenciesPromise: Promise<{
  mapLib: MapLibreLibrary;
  pmtiles: typeof import('pmtiles');
}> | null = null;
let protocol: import('pmtiles').Protocol | null = null;
const registeredArchives = new Map<string, string>();

function loadDependencies() {
  if (!dependenciesPromise) {
    dependenciesPromise = Promise.all([import('maplibre-gl'), import('pmtiles')])
      .then(([mapModule, pmtiles]) => {
        const candidate = (mapModule as unknown as { default?: MapLibreLibrary }).default;
        const mapLib = candidate?.Map ? candidate : mapModule as MapLibreLibrary;
        return { mapLib, pmtiles };
      })
      .catch((error: unknown) => {
        dependenciesPromise = null;
        throw error;
      });
  }
  return dependenciesPromise;
}

function bundledAssetName(url: string): string {
  const path = decodeURIComponent(url.replace(/^bundled:\/\/basemap\//, ''));
  const glyph = /^([^/]+)\/(\d+-\d+)\.pbf$/.exec(path);
  if (!glyph) return path;
  return `${glyph[1].trim().toLowerCase().replace(/\s+/g, '-')}.${glyph[2]}.pbf`;
}

async function loadBundledAsset(
  params: { url: string; type?: 'string' | 'json' | 'arrayBuffer' | 'image' },
  abortController: AbortController,
): Promise<{ data: unknown; cacheControl?: string | null; expires?: string | null }> {
  const name = bundledAssetName(params.url);
  if (!/^[a-z0-9@._-]+$/i.test(name)) throw new Error('Invalid basemap asset');

  const response = await fetch(`${BASEMAP_ROOT}/${name}`, { signal: abortController.signal });
  if (!response.ok) throw new Error('Basemap asset unavailable');

  const data = params.type === 'json' ? await response.json() : await response.arrayBuffer();
  return {
    data,
    cacheControl: response.headers.get('Cache-Control'),
    expires: response.headers.get('Expires'),
  };
}

export async function prepareMapLibre(archives: Readonly<Record<string, string>>): Promise<MapLibreLibrary> {
  const { mapLib, pmtiles } = await loadDependencies();
  if (!protocol) {
    // The bundled style carries the required attribution. Avoid adding the
    // archive's provider metadata as a second, duplicate map label.
    protocol = new pmtiles.Protocol({ metadata: false });
    mapLib.addProtocol('pmtiles', protocol.tile);
    mapLib.addProtocol('bundled', loadBundledAsset);
  }

  for (const [name, url] of Object.entries(archives)) {
    if (registeredArchives.get(name) === url) continue;
    const source = new NamedArchiveSource(name, new pmtiles.FetchSource(url));
    protocol.add(new pmtiles.PMTiles(source));
    registeredArchives.set(name, url);
  }

  return mapLib;
}
